import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  productName: string;
  sku: string;
  onClose: () => void;
}

export default function QuoteRequestModal({ productName, sku, onClose }: Props) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    let honeypot = '';
    const params = new URLSearchParams();
    params.append('product', productName);
    params.append('sku', sku);
    fd.forEach((value, key) => {
      const text = String(value);
      if (key === 'company_alt') {
        honeypot = text.trim();
        return;
      }
      if (text.trim() !== '') params.append(key, text);
    });

    if (honeypot) {
      setSuccess(true);
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      const response = await fetch('https://readdy.ai/api/form/dbdslggdqaaa7daq1msg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      if (response.ok && parsed?.code === 'OK') {
        setSuccess(true);
        form.reset();
        return;
      }
      setFormError(
        serverMsg && serverMsg.trim() !== '' && !serverMsg.includes('form data is spam')
          ? serverMsg
          : t('sfQuote.errorFallback'),
      );
    } catch {
      setFormError(t('sfQuote.errorNetwork'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-start justify-between border-b border-background-200 px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-foreground-950">{t('sfQuote.title')}</h2>
            <p className="mt-0.5 truncate text-[11.5px] text-foreground-500">
              {sku} · {productName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('sfQuote.close')}
            className="cursor-pointer text-foreground-400 transition-colors hover:text-foreground-700"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {success ? (
          <div className="px-5 py-8 text-center">
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-700">
              <i className="ri-checkbox-circle-line text-xl" />
            </span>
            <p className="mt-2.5 text-[13px] font-semibold text-foreground-950">{t('sfQuote.successTitle')}</p>
            <p className="mx-auto mt-1 max-w-[280px] text-[11.5px] leading-5 text-foreground-500">{t('sfQuote.successBody')}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
            >
              {t('sfQuote.closeBtn')}
            </button>
          </div>
        ) : (
          <form id="treejar-storefront-quote" data-readdy-form onSubmit={handleSubmit} className="px-5 py-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block min-w-0">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('sfQuote.fullName')}</span>
                <input
                  name="name"
                  required
                  placeholder={t('sfQuote.yourName')}
                  className="h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                />
              </label>
              <label className="block min-w-0">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('sfQuote.company')}</span>
                <input
                  name="company"
                  required
                  placeholder={t('sfQuote.companyPlaceholder')}
                  className="h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                />
              </label>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block min-w-0">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('sfQuote.email')}</span>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t('sfQuote.emailPlaceholder')}
                  className="h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                />
              </label>
              <label className="block min-w-0">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('sfQuote.quantity')}</span>
                <input
                  type="number"
                  name="quantity"
                  min={1}
                  defaultValue={10}
                  className="tnum h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400"
                />
              </label>
            </div>
            <label className="mt-3 block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('sfQuote.details')}</span>
              <textarea
                name="message"
                rows={3}
                maxLength={500}
                onChange={(e) => setCharCount(e.target.value.length)}
                placeholder={t('sfQuote.detailsPlaceholder')}
                className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-2.5 py-2 text-[12.5px] outline-none focus:border-primary-400"
              />
              <span className="mt-0.5 block text-right text-[10px] text-foreground-400">{charCount}/500</span>
            </label>

            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="aux-field-offscreen"
            />

            {formError && (
              <p className="mt-2 rounded border border-rose-200 bg-rose-50 px-2.5 py-2 text-[11px] leading-4 text-rose-700">
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-3 flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-[12.5px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-[14px]" /> {t('sfQuote.sending')}
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line text-[13px]" /> {t('sfQuote.send')}
                </>
              )}
            </button>
            <p className="mt-2 text-center text-[10px] leading-4 text-foreground-400">{t('sfQuote.note')}</p>
          </form>
        )}
      </div>
    </div>
  );
}